import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, fmtMoney } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useSEO } from "@/lib/seo";
import { useAuth } from "@/contexts/AuthContext";

/**
 * AI Wallet page — user views their credit balance, tops up via Stripe Checkout,
 * and reviews the credit ledger. Deductions are made server-side by future AI
 * jobs via /api/wallet/deduct (admin-only for now).
 */
export default function Wallet() {
  const { user } = useAuth();
  const [wallet, setWallet] = useState(null);
  const [options, setOptions] = useState([]);
  const [enabled, setEnabled] = useState(true);
  const [txns, setTxns] = useState([]);
  const [busy, setBusy] = useState(false);
  const [params, setParams] = useSearchParams();

  useSEO({ title: "AI Wallet | FinanceLocal", noIndex: true });

  const load = () => {
    api.get("/wallet/me").then((r) => setWallet(r.data)).catch(() => setWallet(null));
    api.get("/wallet/transactions").then((r) => setTxns(r.data.items || [])).catch(() => setTxns([]));
    api.get("/wallet/config").then((r) => { setOptions(r.data.topup_options || []); setEnabled(!!r.data.enabled); }).catch(() => {});
  };
  useEffect(load, []);

  // Poll payment status when returning from Stripe (?wallet_session=...)
  useEffect(() => {
    const sid = params.get("wallet_session");
    if (!sid) return;
    let live = true;
    let attempts = 0;
    const poll = async () => {
      try {
        const r = await api.get(`/wallet/status/${sid}`);
        if (r.data.payment_status === "paid") {
          toast.success(`Top-up complete — balance ${fmtMoney(r.data.wallet_balance || 0)}`);
          const next = new URLSearchParams(params);
          next.delete("wallet_session");
          setParams(next, { replace: true });
          load();
          return;
        }
        if (r.data.status === "expired") {
          toast.error("Payment session expired");
          const next = new URLSearchParams(params);
          next.delete("wallet_session");
          setParams(next, { replace: true });
          return;
        }
      } catch {}
      if (live && attempts++ < 8) setTimeout(poll, 2000);
    };
    poll();
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  const topUp = async (amount) => {
    setBusy(true);
    try {
      const r = await api.post("/wallet/checkout", {
        amount, origin_url: window.location.origin,
      });
      if (r.data.url) window.location.href = r.data.url;
      else throw new Error("No checkout URL");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to start top-up");
      setBusy(false);
    }
  };

  if (!user) return null;

  return (
    <div data-testid="page-wallet" className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 py-10">
      <div className="border-b border-border pb-6 mb-8">
        <div className="font-overline text-accent">Account</div>
        <h1 className="font-display text-4xl sm:text-5xl mt-2">AI Wallet</h1>
        <p className="text-sm text-muted-foreground mt-2">
          Credits power AI-assisted features on FinanceLocal (coming soon). Top up via secure Stripe Checkout.
        </p>
      </div>

      {/* Balance panel */}
      <div className="grid md:grid-cols-3 border-l border-t border-border mb-8">
        <Tile label="Current Balance" value={fmtMoney(wallet?.balance || 0)} tone="primary" testid="wallet-balance" />
        <Tile label="Total Purchased" value={fmtMoney(wallet?.total_purchased || 0)} testid="wallet-purchased" />
        <Tile label="Total Used"      value={fmtMoney(wallet?.total_used || 0)} testid="wallet-used" />
      </div>

      {/* Top-up options */}
      {enabled ? (
        <section className="mb-10" data-testid="wallet-topup-section">
          <div className="font-overline text-accent mb-3">Add Funds</div>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            {options.map((amt) => (
              <Button
                key={amt}
                onClick={() => topUp(amt)}
                disabled={busy}
                className="rounded-none h-16 font-mono-data text-lg uppercase"
                data-testid={`wallet-topup-${amt}`}
                variant="outline"
              >
                {fmtMoney(amt)}
              </Button>
            ))}
          </div>
          <p className="text-[11px] text-muted-foreground mt-3">
            Powered by Stripe. Amounts are non-refundable unless a technical issue prevents credit application.
          </p>
        </section>
      ) : (
        <div className="border border-border p-6 mb-10 text-center text-muted-foreground text-sm" data-testid="wallet-disabled">
          Wallet top-ups are temporarily paused. Check back later.
        </div>
      )}

      {/* Transaction history */}
      <section>
        <div className="font-overline text-accent mb-3">Transaction History</div>
        <div className="border border-border overflow-x-auto" data-testid="wallet-history">
          {txns.length === 0 ? (
            <div className="p-6 text-center text-muted-foreground text-sm" data-testid="wallet-history-empty">No transactions yet.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-left">
                <tr>
                  <Th>When</Th><Th>Type</Th><Th>Amount</Th><Th>Reason</Th><Th>Balance After</Th>
                </tr>
              </thead>
              <tbody>
                {txns.map((t) => (
                  <tr key={t.wallet_tx_id} className="border-t border-border" data-testid={`wallet-tx-${t.wallet_tx_id}`}>
                    <Td className="font-mono text-[10px] whitespace-nowrap">{t.at ? new Date(t.at).toLocaleString() : "—"}</Td>
                    <Td><Badge type={t.type} /></Td>
                    <Td className={`font-mono-data ${t.amount < 0 ? "text-red-600" : "text-emerald-600"}`}>
                      {t.amount >= 0 ? "+" : ""}{fmtMoney(t.amount)}
                    </Td>
                    <Td className="text-[11px] text-muted-foreground">{t.reason || "—"}</Td>
                    <Td className="font-mono-data">{fmtMoney(t.balance_after || 0)}</Td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </section>
    </div>
  );
}

const Th = ({ children }) => <th className="px-3 py-2 font-overline text-[10px] text-muted-foreground">{children}</th>;
const Td = ({ children, className = "" }) => <td className={`px-3 py-2 ${className}`}>{children}</td>;

function Tile({ label, value, tone, testid }) {
  const cls = tone === "primary" ? "text-primary" : "";
  return (
    <div className="p-6 border-r border-b border-border" data-testid={testid}>
      <div className="font-overline text-[10px] text-muted-foreground">{label}</div>
      <div className={`font-mono-data text-3xl mt-2 font-light ${cls}`}>{value}</div>
    </div>
  );
}

function Badge({ type }) {
  const tone =
    type === "deposit" ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300" :
    type === "refund"  ? "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300" :
    "bg-muted text-muted-foreground";
  return <span className={`text-[10px] font-mono uppercase px-2 py-0.5 rounded-none ${tone}`}>{type}</span>;
}
